import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import { Navigate } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { Room } from "../entities/room.entity";
import { useCountRoomsQuery, useGetRoomsQuery } from "../services/rooms.service";
import { FilterRoomsInputs } from "../types";
import { RoomsTable } from "../components/rooms/table";
import { RoomsSearch } from "../components/rooms/search";
import { FaBroadcastTower, FaComments, FaCalendarAlt } from "react-icons/fa";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

const StatCard = ({ title, count, icon, colorClass, borderClass, bgClass }: any) => (
  <div className={`p-4 rounded-xl border ${borderClass} ${bgClass} shadow-sm flex items-center justify-between`}>
    <div>
      <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-gray-800">{count ?? "-"}</h3>
    </div>
    <div className={`p-3 rounded-full ${colorClass} bg-white bg-opacity-60`}>
      {icon}
    </div>
  </div>
);

const Rooms = () => {
  const tableSearch = usePaginatedTableSearchParams<FilterRoomsInputs>({
    parseState: (searchParams) => {
      const query: FilterRoomsInputs = {};
      const id = searchParams.get("id");
      const userId = searchParams.get("userId");
      const status = searchParams.get("status");
      const category = searchParams.get("category");
      const privacy = searchParams.get("privacy");
      const langCode = searchParams.get("langCode");

      if (id) query.id = id;
      if (userId) query.userId = userId;
      if (status) query.status = status as FilterRoomsInputs["status"];
      if (category) query.category = category as FilterRoomsInputs["category"];
      if (privacy) query.privacy = privacy as FilterRoomsInputs["privacy"];
      if (langCode) query.langCode = langCode;

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.id) nextParams.set("id", state.id);
      if (state.userId) nextParams.set("userId", state.userId);
      if (state.status) nextParams.set("status", state.status);
      if (state.category) nextParams.set("category", state.category);
      if (state.privacy) nextParams.set("privacy", state.privacy);
      if (state.langCode) nextParams.set("langCode", state.langCode);
      return nextParams;
    },
  });

  const initialQuery = tableSearch.state;

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError } =
    usePagination<Room>({
      useGetDataQuery: useGetRoomsQuery,
      useCountData: useCountRoomsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  // Fetch stats separately
  const { data: totalRoomsCount } = useCountRoomsQuery({ where: {} });
  const { data: activeRoomsCount } = useCountRoomsQuery({ where: { status: "ACTIVE" } });
  const { data: communityRoomsCount } = useCountRoomsQuery({ where: { category: "COMMUNITY" } });
  const { data: scheduledRoomsCount } = useCountRoomsQuery({ where: { status: "SCHEDULED" } });

  return (
    <div className="page_container space-y-6">
      <PageHeader text="Rooms" />

      {/* Stats Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Rooms"
          count={totalRoomsCount}
          icon={<FaBroadcastTower className="text-xl text-purple-600" />}
          colorClass="text-purple-600"
          borderClass="border-purple-100"
          bgClass="bg-gradient-to-br from-purple-50 to-indigo-50"
        />
        <StatCard
          title="Active Rooms"
          count={activeRoomsCount}
          icon={<FaBroadcastTower className="text-xl text-green-600" />}
          colorClass="text-green-600"
          borderClass="border-green-100"
          bgClass="bg-gradient-to-br from-green-50 to-emerald-50"
        />
        <StatCard
          title="Community Rooms"
          count={communityRoomsCount}
          icon={<FaComments className="text-xl text-blue-600" />}
          colorClass="text-blue-600"
          borderClass="border-blue-100"
          bgClass="bg-gradient-to-br from-blue-50 to-indigo-50"
        />
        <StatCard
          title="Scheduled Rooms"
          count={scheduledRoomsCount}
          icon={<FaCalendarAlt className="text-xl text-amber-600" />}
          colorClass="text-amber-600"
          borderClass="border-amber-100"
          bgClass="bg-gradient-to-br from-amber-50 to-orange-50"
        />
      </div>

      <div className="border border-gray-300 rounded-lg p-4 mb-4">
        <RoomsSearch updateFilterQuery={tableSearch.replaceFilters} initialQuery={initialQuery} />
      </div>

      {isSuccess ? (
        <>
          <RoomsTable data={data} />
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} />
        </>
      ) : isError ? (
        <Navigate to="/505" />
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default Rooms;
